import React from "react";

/**
 * Makes a non-semantic clickable element (div/span) keyboard-accessible.
 * Spread the returned props onto the element instead of a bare onClick:
 *
 *     <div className={styles.card} {...onActivate(() => navigate("/x"))}>
 *
 * Adds role="button", makes it tabbable, and activates on Enter or Space —
 * matching native button behavior. Prefer converting to a real
 * <button type="button" className="btn-unstyled"> where the markup allows;
 * use this helper where a real button would fight the existing layout
 * (cards, table rows, complex containers).
 */
export const onActivate = (handler: (event?: any) => void) => ({
    role: "button" as const,
    tabIndex: 0,
    onClick: (event: React.MouseEvent) => handler(event),
    onKeyDown: (event: React.KeyboardEvent) => {
        // Ignore keystrokes bubbling up from focusable children (e.g. a real
        // button nested inside a clickable card) — they handle Enter themselves.
        if (event.target !== event.currentTarget) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            // Space would otherwise scroll the page; Enter can submit forms.
            event.preventDefault();
            handler(event);
        }
    },
});
